/**
 * CorporateBookingModal — a corporate admin books a ride on behalf of a staff
 * member, billed to the company account. Reuses the same PlaceAutocomplete +
 * StopsEditor as the dispatcher form; payment is always the company account.
 */
import React, { useMemo, useState } from "react";
import { X, User, Loader2, MapPin } from "lucide-react";
import { PlaceAutocomplete } from "../../components/PlaceAutocomplete";
import { StopsEditor } from "../../components/passenger/BookingFormHelpers";
import { Button } from "../../components/ui/button";

export const CorporateBookingModal = ({ staff = [], costCentres = [], onClose, onCreate }) => {
  const bookable = useMemo(() => staff.filter((s) => !s.disabled && (s.phone || "").trim()), [staff]);
  const [staffId, setStaffId] = useState("");
  const [costCentreId, setCostCentreId] = useState("");
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [stops, setStops] = useState([]);
  const [passengerCount, setPassengerCount] = useState(1);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectedStaff = useMemo(() => bookable.find((s) => s.id === staffId), [bookable, staffId]);
  const inheritedCcName = useMemo(() => {
    const id = selectedStaff?.default_cost_centre_id;
    if (!id) return null;
    return costCentres.find((c) => c.id === id)?.name || selectedStaff?.default_cost_centre_name || null;
  }, [selectedStaff, costCentres]);

  const canSubmit = staffId && pickup?.lat && dropoff?.lat && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onCreate({
        staff_user_id: staffId,
        pickup_address: pickup.name, pickup_lat: pickup.lat, pickup_lng: pickup.lng,
        dropoff_address: dropoff.name, dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
        stops: stops.filter((s) => s && s.lat != null).map((s) => ({ address: s.name, lat: s.lat, lng: s.lng })),
        passenger_count: passengerCount,
        driver_notes: notes.trim() || null,
        cost_centre_id: costCentreId || null,
      });
      onClose();
    } catch (e) {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="corporate-booking-modal" onMouseDown={onClose}>
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto border border-border" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-white">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight">Book a ride for staff</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Billed to your company account.</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="corporate-booking-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="label-mini text-muted-foreground">Staff member</label>
            {bookable.length === 0 ? (
              <p className="text-xs text-[#92400E] bg-[#FFFBEB] border border-[#FCD34D] p-2 mt-1" data-testid="corporate-booking-nostaff">
                No bookable staff. Add a staff member with a phone number (and make sure they're enabled) first.
              </p>
            ) : (
              <div className="flex items-center px-3 py-2 border border-border bg-white mt-1">
                <User className="w-4 h-4 text-muted-foreground mr-2" />
                <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className="flex-1 outline-none bg-transparent text-sm" data-testid="corporate-booking-staff-select">
                  <option value="">Select staff…</option>
                  {bookable.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.phone}</option>)}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="label-mini text-muted-foreground">Cost centre (optional)</label>
            <div className="flex items-center px-3 py-2 border border-border bg-white mt-1">
              <select value={costCentreId} onChange={(e) => setCostCentreId(e.target.value)} className="flex-1 outline-none bg-transparent text-sm" data-testid="corporate-booking-cost-centre">
                <option value="">{inheritedCcName ? `Inherit from staff — ${inheritedCcName}` : "None"}</option>
                {costCentres.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="label-mini text-muted-foreground flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Pickup</label>
            <div className="border border-border mt-1">
              <PlaceAutocomplete value={pickup} onChange={setPickup} placeholder="Pickup address…" testId="corporate-booking-pickup" icon={<MapPin className="w-4 h-4 text-[#10B981] mr-2" />} />
            </div>
          </div>

          <div>
            <label className="label-mini text-muted-foreground flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]" /> Destination</label>
            <div className="border border-border mt-1">
              <PlaceAutocomplete value={dropoff} onChange={setDropoff} placeholder="Destination address…" testId="corporate-booking-dropoff" icon={<MapPin className="w-4 h-4 text-[#EF4444] mr-2" />} />
            </div>
          </div>

          <div>
            <label className="label-mini text-muted-foreground">Extra stops (optional)</label>
            <div className="border border-border mt-1"><StopsEditor stops={stops} onChange={setStops} max={3} /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mini text-muted-foreground">Passengers</label>
              <select value={passengerCount} onChange={(e) => setPassengerCount(Number(e.target.value))} className="w-full mt-1 px-3 py-2 border border-border bg-white text-sm outline-none" data-testid="corporate-booking-passengers">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} passenger{n > 1 ? "s" : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mini text-muted-foreground">Billing</label>
              <div className="w-full mt-1 px-3 py-2 border border-border bg-secondary text-sm text-muted-foreground" data-testid="corporate-booking-billing">Company account</div>
            </div>
          </div>

          <div>
            <label className="label-mini text-muted-foreground">Notes for driver (optional)</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. meet at reception" className="w-full mt-1 px-3 py-2 border border-border text-sm outline-none" data-testid="corporate-booking-notes" />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} data-testid="corporate-booking-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} className="bg-[#002FA7] hover:bg-[#002685] text-white disabled:opacity-50" data-testid="corporate-booking-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Booking…</> : "Book & dispatch"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CorporateBookingModal;
